import { Injectable, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { map, shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';

import { OnSiteLocationStoreSelector } from '@common/selectors/on-site-location.selector';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { OnSiteLocationsService } from '@core/services/on-site-locations/on-site-locations.service';
import { DoodOrderModel } from '@store/order/order.model';

import {
  ORDER_STATUS_BLOCK_LOADING_VM,
  OrderStatusBlockDataSource,
  OrderStatusBlockViewModel,
} from './order-status-block-view-model';

@Injectable()
export class OrderStatusBlockStoreDataSource implements OrderStatusBlockDataSource, OnDestroy {
  private readonly orderSelector = inject(OrderStoreSelector);
  private readonly onSiteLocationSelector = inject(OnSiteLocationStoreSelector);
  private readonly onSiteLocationsService = inject(OnSiteLocationsService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly destroy$ = new Subject<void>();
  private readonly orderId = this.activatedRoute.snapshot.params.orderId;

  private readonly orders$: Observable<readonly DoodOrderModel[]> = this.orderSelector
    .selectOrder(this.orderId)
    .pipe(
      switchMap(order => {
        if (!order) {
          return of([] as DoodOrderModel[]);
        }
        if (order.multi_shop) {
          return this.orderSelector.selectMultipleOrders((order.child_orders || []).map(c => c.id));
        }
        return of([order]);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

  private readonly onSiteLocationName$: Observable<string> = this.orderSelector.selectActive.pipe(
    switchMap(order => {
      if (order?.onsite_location_id) {
        return this.onSiteLocationSelector.selectLocation(order.onsite_location_id);
      }
      return of(null);
    }),
    map(location => location?.name ?? ''),
  );

  readonly vm$: Observable<OrderStatusBlockViewModel> = combineLatest([
    this.orders$,
    this.onSiteLocationName$,
  ]).pipe(
    map(([orders, onSiteLocationName]) =>
      orders.length
        ? { orders, onSiteLocationName }
        : { ...ORDER_STATUS_BLOCK_LOADING_VM, onSiteLocationName },
    ),
  );

  constructor() {
    this.loadOrderShopSlots();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadOrderShopSlots(): void {
    this.orderSelector
      .selectOrder(this.orderId)
      .pipe(
        take(1),
        switchMap(order => {
          if (order?.onsite_location_id && order.shop) {
            return this.onSiteLocationsService.loadOnSiteLocations$(order.shop.id);
          }
          return of(null);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }
}
